import { useState, useEffect, useCallback } from 'react'
import { MainLayout } from './components/Layout'
import { AudioPlayer } from './components/Player'
import { BroadcastInfo } from './components/BroadcastInfo'
import { Chat } from './components/Chat'
import { AboutModal } from './components/AboutModal'
import { Broadcast } from './types'

// Stream URLs for YesStreaming/Icecast
interface StreamUrls {
  primary: string
  fallback: string
}

// PWA install prompt event (Chrome/Android)
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function App() {
  const [broadcast, setBroadcast] = useState<Broadcast | null>(null)
  const [streamUrls, setStreamUrls] = useState<StreamUrls | null>(null)
  const [loading, setLoading] = useState(true)
  const [autoDjText, setAutoDjText] = useState<string>('')
  const [autoDjSubtitle, setAutoDjSubtitle] = useState<string>('')
  const [aboutTitle, setAboutTitle] = useState<string>('')
  const [aboutBody, setAboutBody] = useState<string>('')
  const [showAbout, setShowAbout] = useState(false)
  const [autoDjTrack, setAutoDjTrack] = useState<string | null>(null)
  const [autoDjPlaylist, setAutoDjPlaylist] = useState<string | null>(null)

  // PWA install prompt state
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showInstallBanner, setShowInstallBanner] = useState(false)

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

  // Capture beforeinstallprompt for manual triggering
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed) return

    const handler = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as BeforeInstallPromptEvent)
      setShowInstallBanner(true)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const handleInstall = async () => {
    if (!installPrompt) return
    setShowInstallBanner(false)
    await installPrompt.prompt()
    const { outcome } = await installPrompt.userChoice
    if (outcome === 'dismissed') {
      localStorage.setItem('pwa-install-dismissed', '1')
    }
    setInstallPrompt(null)
  }

  const handleDismissInstall = () => {
    setShowInstallBanner(false)
    localStorage.setItem('pwa-install-dismissed', '1')
    setInstallPrompt(null)
  }

  // Fetch stream URLs from backend
  const fetchStreamUrl = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stream-url`)
      const data = await response.json()

      if (data.success) {
        setStreamUrls({
          primary: data.url,
          fallback: data.fallback,
        })
        console.log('Stream URLs obtained:', data.url)
      } else {
        console.error('Failed to get stream URL:', data.error)
        // Use default YesStreaming URLs as fallback (paid account)
        setStreamUrls({
          primary: 'https://ec3.yesstreaming.net:3005/stream',
          fallback: 'https://ec3.yesstreaming.net:3005/stream',
        })
      }
    } catch (err) {
      console.error('Error fetching stream URL:', err)
      // Use default YesStreaming URLs as fallback (paid account)
      setStreamUrls({
        primary: 'https://ec3.yesstreaming.net:3005/stream',
        fallback: 'https://ec3.yesstreaming.net:3005/stream',
      })
    }
  }, [API_URL])

  const fetchCurrentBroadcast = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/broadcast/current`)
      const data = await response.json()

      if (data.success && data.broadcast) {
        const newBroadcast = data.broadcast
        setBroadcast(newBroadcast)
      } else {
        setBroadcast(null)
      }
    } catch (err) {
      console.error('Error fetching broadcast:', err)
      setBroadcast(null)
    } finally {
      setLoading(false)
    }
  }, [API_URL])

  // Fetch station settings (one-time on mount)
  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`)
      const data = await response.json()
      if (data.success && data.settings) {
        if (data.settings.auto_dj_text) setAutoDjText(data.settings.auto_dj_text)
        if (data.settings.auto_dj_subtitle) setAutoDjSubtitle(data.settings.auto_dj_subtitle)
        if (data.settings.about_title) setAboutTitle(data.settings.about_title)
        if (data.settings.about_body) setAboutBody(data.settings.about_body)
      }
    } catch (err) {
      console.error('Error fetching settings:', err)
    }
  }, [API_URL])

  // Fetch stream URLs and settings on mount
  useEffect(() => {
    fetchStreamUrl()
    fetchSettings()
  }, [fetchStreamUrl, fetchSettings])

  useEffect(() => {
    fetchCurrentBroadcast()
    const interval = setInterval(fetchCurrentBroadcast, 10000) // Refresh every 10 seconds
    return () => clearInterval(interval)
  }, [fetchCurrentBroadcast])

  useEffect(() => {
    if (broadcast) {
      setAutoDjTrack(null)
      setAutoDjPlaylist(null)
      return
    }

    const fetchAutoDjNowPlaying = async () => {
      try {
        const response = await fetch(`${API_URL}/api/autodj/now-playing`)
        const data = await response.json()
        if (data.success && data.nowPlaying) {
          setAutoDjTrack(data.nowPlaying.trackName)
          setAutoDjPlaylist(data.nowPlaying.playlistTitle)
        }
      } catch (err) {
        console.error('Error fetching AutoDJ now playing:', err)
      }
    }

    fetchAutoDjNowPlaying()
    const interval = setInterval(fetchAutoDjNowPlaying, 20000)
    return () => clearInterval(interval)
  }, [broadcast, API_URL])

  if (loading) {
    return (
      <MainLayout>
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl mb-4 animate-pulse font-bold text-[var(--text-primary)]">RADIO NATKA</div>
            <p className="text-[var(--text-secondary)]">טוען...</p>
          </div>
        </div>
      </MainLayout>
    )
  }

  return (
    <MainLayout onAboutClick={() => setShowAbout(true)}>
      {/* PWA Install Banner — subtle, one-time, dismissible */}
      {showInstallBanner && (
        <div
          dir="rtl"
          className="flex items-center justify-between gap-3 px-4 py-2 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] text-sm"
        >
          <span className="text-[var(--text-secondary)]">
            📲 התקן את האפליקציה למסך הבית לחוויה טובה יותר
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleInstall}
              className="px-3 py-1 rounded bg-[var(--accent)] text-white text-xs font-medium hover:bg-[var(--accent-hover)] transition-colors"
            >
              התקן
            </button>
            <button
              onClick={handleDismissInstall}
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-lg leading-none"
              aria-label="סגור"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="h-full flex flex-col lg:grid lg:grid-cols-[1fr_380px] gap-0">
        {/* Main Content Area - Player and Broadcast Info */}
        <div className="flex-1 flex flex-col bg-[var(--bg-primary)] p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-3xl mx-auto w-full space-y-4 sm:space-y-6">
            {/* Player section */}
            <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 sm:p-8">
              <AudioPlayer
                streamUrl={streamUrls?.primary || ''}
                fallbackUrl={streamUrls?.fallback}
                broadcast={broadcast}
              />
            </div>

            {/* Broadcast Info section */}
            <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 sm:p-6">
              <BroadcastInfo broadcast={broadcast} autoDjText={autoDjText} autoDjSubtitle={autoDjSubtitle} autoDjCurrentTrack={autoDjTrack} autoDjPlaylistName={autoDjPlaylist} />
            </div>
          </div>
        </div>

        {/* Chat Sidebar - Full height on mobile, sidebar on desktop */}
        <div className="bg-[var(--bg-secondary)] border-t lg:border-t-0 lg:border-r border-[var(--border-color)] flex flex-col h-[50vh] lg:h-full">
          <div className="p-3 sm:p-4 border-b border-[var(--border-color)]">
            <h2 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)]">צ'אט</h2>
          </div>
          <div className="flex-1 overflow-hidden">
            <Chat isLive={broadcast?.is_live || false} />
          </div>
        </div>
      </div>

      <AboutModal
        isOpen={showAbout}
        onClose={() => setShowAbout(false)}
        title={aboutTitle}
        body={aboutBody}
      />
    </MainLayout>
  )
}

export default App
